import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { QEImage, QuoteExperienceState } from "../../QuoteExperienceQuickSetup.page";
import { ApiServiceContext } from "../../../../api/ApiService.context";
import { QuoteExperienceService } from "../../../../api/quote-experience/QuoteExperienceService.class";
import { BrandImage } from "./BrandImage.component";
import { Box, Typography } from "@mui/material";

export const BrandImagesManager: FC<{
    initial: Partial<QuoteExperienceState>;
    current: Partial<QuoteExperienceState>;
    bundleGuid: string | undefined;
    setIsLoading: (key: string, value: boolean) => void;
    updateData: (pointer: string, value: any, setInitial?: boolean, skipCurrent?: boolean) => void;
}> = ({
    initial,
    current,
    bundleGuid,
    setIsLoading,
    updateData
}) => {
        const { getApiServiceCollections } = useContext(ApiServiceContext);
        const { QuoteExperienceService } = getApiServiceCollections() as {
            QuoteExperienceService: QuoteExperienceService;
        };

        const [managedAssets, setManagedAssets] = useState<Record<string, QEImage>>({});
        const managedAssetsArray = useMemo(() => Object.entries(managedAssets), [managedAssets]);
        const [assetSource, setAssetSource] = useState<'NOT STARTED' | 'LOCAL' | 'SERVER'>('NOT STARTED');
        const [brandImagesLoading, setBrandImagesLoading] = useState<'NOT STARTED' | 'LOADING' | 'DONE'>('NOT STARTED');
        const [logoIdxMapping, setLogoIdxMapping] = useState<Record<string, number>>({});
        const [faviconPngDataUri, setFaviconPngDataUri] = useState<string | ArrayBuffer | undefined>();
        const [faviconIcoDataUri, setFaviconIcoDataUri] = useState<string | ArrayBuffer | undefined>();

        const renderLogos = useMemo(() => 
            assetSource !== 'NOT STARTED' && (assetSource !== 'SERVER' || !bundleGuid || brandImagesLoading === 'DONE')
        , [assetSource, bundleGuid, brandImagesLoading]);

        useEffect(() => {
            if (!bundleGuid || assetSource !== 'SERVER' || brandImagesLoading !== 'NOT STARTED') {
                return
            }
            setBrandImagesLoading('LOADING');
            console.log('Fetching Brand Images');
            setIsLoading('BrandImages', true);
            const fetchData = async () => {
                const [desktopLogoResp, mobileLogoResp, faviconBaseResp, faviconPngResp, faviconIcoResp] = await Promise.all([
                    QuoteExperienceService.loadImage(bundleGuid, 'assets/logos/desktop-logo.svg'),
                    QuoteExperienceService.loadImage(bundleGuid, 'assets/logos/mobile-logo.svg'),
                    QuoteExperienceService.loadImage(bundleGuid, 'assets/favicon/favicon.svg'),
                    QuoteExperienceService.loadImage(bundleGuid, 'assets/favicon/favicon-96x96.png'),
                    QuoteExperienceService.loadImage(bundleGuid, 'assets/favicon/favicon.ico'),
                ]);

                const assetsToPatch: Record<string, QEImage> = {
                    'desktop': {
                        id: 'assets/logos/desktop-logo.svg',
                        file: desktopLogoResp,
                        name: 'Desktop Logo',
                        previewUrl: desktopLogoResp ? URL.createObjectURL(desktopLogoResp) : undefined,
                        error: null,
                        path: 'assets/logos/desktop-logo.svg',
                        hint: 'Main logo for desktop view'
                    },
                    'mobile': {
                        id: 'assets/logos/mobile-logo.svg',
                        file: mobileLogoResp,
                        name: 'Mobile Logo',
                        previewUrl: mobileLogoResp ? URL.createObjectURL(mobileLogoResp) : undefined,
                        error: null,
                        path: 'assets/logos/mobile-logo.svg',
                        hint: 'Compact logo for mobile'
                    }
                };

                const faviconAssets = {
                    'favicon': {
                        id: 'assets/favicon/favicon.svg',
                        file: faviconBaseResp,
                        name: 'Favicon',
                        previewUrl: faviconBaseResp ? URL.createObjectURL(faviconBaseResp) : undefined,
                        error: null,
                        path: 'assets/favicon/favicon.svg',
                        hint: 'Browser tab icon'
                    },
                    'favicon-png': {
                        id: 'assets/favicon/favicon-96x96.png',
                        file: faviconPngResp,
                        name: 'Favicon PNG',
                        previewUrl: faviconPngResp ? URL.createObjectURL(faviconPngResp) : undefined,
                        error: null,
                        path: 'assets/favicon/favicon-96x96.png',
                        hint: 'Browser tab icon'
                    },
                    'favicon-ico': {
                        id: 'assets/favicon/favicon.ico',
                        file: faviconIcoResp,
                        name: 'Favicon ICO',
                        previewUrl: faviconIcoResp ? URL.createObjectURL(faviconIcoResp) : undefined,
                        error: null,
                        path: 'assets/favicon/favicon.ico',
                        hint: 'Browser tab icon'
                    }
                };

                setFaviconPngDataUri(faviconAssets['favicon-png'].previewUrl);
                setFaviconIcoDataUri(faviconAssets['favicon-ico'].previewUrl);

                updateData('/logos', Object.values(assetsToPatch), true, true);
                updateData('/favicon', Object.values(faviconAssets), true, true);

                setManagedAssets({
                    ...assetsToPatch,
                    'favicon': faviconAssets['favicon']
                });
                setLogoIdxMapping({
                    'desktop': -1,
                    'mobile': -1,
                    'favicon': -1,
                    'favicon-png': -1,
                    'favicon-ico': -1,
                });
                setIsLoading('BrandImages', false);
                setBrandImagesLoading('DONE');
            }

            fetchData();
            return () => {
                // Any necessary cleanup
            };
        }, [bundleGuid, brandImagesLoading, assetSource, setIsLoading, updateData, QuoteExperienceService]);

        useEffect(() => {
            if (assetSource !== 'NOT STARTED') {
                return
            }
            const initialDesktop = (initial.logos || []).find(image => image.path === 'assets/logos/desktop-logo.svg');
            const initialMobile = (initial.logos || []).find(image => image.path === 'assets/logos/mobile-logo.svg');
            const initialFavicon = (initial.favicon || []).find(image => image.path === 'assets/favicon/favicon.svg');
            const initialFaviconPng = (initial.favicon || []).find(image => image.path === 'assets/favicon/favicon-96x96.png');
            const initialFaviconIco = (initial.favicon || []).find(image => image.path === 'assets/favicon/favicon.ico');

            const currentDesktopIdx = (current.logos || []).findIndex(image => image.path === 'assets/logos/desktop-logo.svg');
            const currentMobileIdx = (current.logos || []).findIndex(image => image.path === 'assets/logos/mobile-logo.svg');
            const currentFaviconIdx = (current.favicon || []).findIndex(image => image.path === 'assets/favicon/favicon.svg');
            const currentFaviconPngIdx = (current.favicon || []).findIndex(image => image.path === 'assets/favicon/favicon-96x96.png');
            const currentFaviconIcoIdx = (current.favicon || []).findIndex(image => image.path === 'assets/favicon/favicon.ico');

            const currentDesktop = currentDesktopIdx > -1 ? current.logos![currentDesktopIdx] : undefined;
            const currentMobile = currentMobileIdx > -1 ? current.logos![currentMobileIdx] : undefined;
            const currentFavicon = currentFaviconIdx > -1 ? current.favicon![currentFaviconIdx] : undefined;
            const currentFaviconPng = currentFaviconPngIdx > -1 ? current.favicon![currentFaviconPngIdx] : undefined;
            const currentFaviconIco = currentFaviconIcoIdx > -1 ? current.favicon![currentFaviconIcoIdx] : undefined;

            let localAssets: Record<string, QEImage> = {
                'desktop': currentDesktop || initialDesktop || {
                    id: 'assets/logos/desktop-logo.svg',
                    file: undefined,
                    name: 'Desktop Logo',
                    previewUrl: undefined,
                    error: null,
                    path: 'assets/logos/desktop-logo.svg',
                    hint: 'Main logo for desktop view'
                },
                'mobile': currentMobile || initialMobile || {
                    id: 'assets/logos/mobile-logo.svg',
                    file: undefined,
                    name: 'Mobile Logo',
                    previewUrl: undefined,
                    error: null,
                    path: 'assets/logos/mobile-logo.svg',
                    hint: 'Compact logo for mobile'
                },
                'favicon': currentFavicon || initialFavicon || {
                    id: 'assets/favicon/favicon.svg',
                    file: undefined,
                    name: 'Favicon',
                    previewUrl: undefined,
                    error: null,
                    path: 'assets/favicon/favicon.svg',
                    hint: 'Browser tab icon'
                },
                'favicon-png': currentFaviconPng || initialFaviconPng || {
                    id: 'assets/favicon/favicon-96x96.png',
                    file: undefined,
                    name: 'Favicon PNG',
                    previewUrl: undefined,
                    error: null,
                    path: 'assets/favicon/favicon-96x96.png',
                    hint: 'Browser tab icon'
                },
                'favicon-ico': currentFaviconIco || initialFaviconIco || {
                    id: 'assets/favicon/favicon.ico',
                    file: undefined,
                    name: 'Favicon ICO',
                    previewUrl: undefined,
                    error: null,
                    path: 'assets/favicon/favicon.ico',
                    hint: 'Browser tab icon'
                }
            };
            const {
                "favicon-png": faviconPngAsset,
                "favicon-ico": faviconIcoAsset,
                ...remainingAssets
            } = localAssets;
            setFaviconPngDataUri(faviconPngAsset.previewUrl);
            setFaviconIcoDataUri(faviconIcoAsset.previewUrl);
            setManagedAssets(remainingAssets);
            setLogoIdxMapping({
                'desktop': currentDesktopIdx,
                'mobile': currentMobileIdx,
                'favicon': currentFaviconIdx,
                'favicon-png': currentFaviconPngIdx,
                'favicon-ico': currentFaviconIcoIdx,
            });
            setAssetSource(Object.values(localAssets).find(image => !!image.file) ? 'LOCAL' : 'SERVER');
        }, [assetSource, current, initial]);

        const onFaviconProcessed = useCallback((pngBlob: Blob, icoBlob: Blob) => {
            updateData('/favicon', (currentFavicons: QEImage[]) => {
                const newFavicons = [
                    ...currentFavicons
                ];
                const faviconsToAdd: QEImage[] = [];
                const newFaviconPng = {
                    id: 'assets/favicon/favicon-96x96.png',
                    file: pngBlob,
                    name: 'Favicon PNG',
                    previewUrl: pngBlob ? URL.createObjectURL(pngBlob) : undefined,
                    error: null,
                    path: 'assets/favicon/favicon-96x96.png',
                };
                const newFaviconIco = {
                    id: 'assets/favicon/favicon.ico',
                    file: icoBlob,
                    name: 'Favicon ICO',
                    previewUrl: icoBlob ? URL.createObjectURL(icoBlob) : undefined,
                    error: null,
                    path: 'assets/favicon/favicon.ico',
                }
                const pngIdx = newFavicons.findIndex(image => image.path === 'assets/favicon/favicon-96x96.png');
                const icoIdx = newFavicons.findIndex(image => image.path === 'assets/favicon/favicon.ico');
                if (pngIdx > -1) {
                    newFavicons[pngIdx] = newFaviconPng;
                } else {
                    faviconsToAdd.push(newFaviconPng);
                }
                if (icoIdx > -1) {
                    newFavicons[icoIdx] = newFaviconIco;
                } else {
                    faviconsToAdd.push(newFaviconIco);
                }
                newFavicons.push(...faviconsToAdd);
                setLogoIdxMapping(prev => ({
                    ...prev,
                    'favicon-png': pngIdx,
                    'favicon-ico': icoIdx
                }));
                return newFavicons;
            });
        }, [updateData]);

        useEffect(() => {
            console.log('Current images', JSON.stringify(current.images || []));
            console.log('Current logos', JSON.stringify(current.logos || []));
            console.log('Current favicon', JSON.stringify(current.favicon || []));
        }, [current]);

        return (
            <Box sx={{ mt: 2, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Branding Assets
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Upload SVG files for your branding. These will appear in the quote experience.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                    {
                        renderLogos
                        ? managedAssetsArray.map(([key, asset]) => (
                            <BrandImage
                                key={key}
                                hint={asset.hint || 'Image'}
                                imageName={asset.name}
                                initialBaseImage={asset.file}
                                initialBaseImageUrl={asset.previewUrl}
                                onImageChange={(image: File | Blob) => {
                                    const assetBasePath = key === 'favicon' ? 'favicon' : 'logos';
                                    if (logoIdxMapping[key] > -1) {
                                        console.log(`Updating ${assetBasePath} at index: ${logoIdxMapping[key]}`);
                                        updateData(`/${assetBasePath}/${logoIdxMapping[key]}`, {
                                            ...asset,
                                            file: image,
                                            previewUrl: image ? URL.createObjectURL(image) : undefined,
                                        });
                                    } else {
                                        console.log(`Adding new image to ${assetBasePath}`);
                                        updateData(`/${assetBasePath}`, (images: QEImage[]) => ([
                                            ...images,
                                            {
                                                ...asset,
                                                file: image,
                                                previewUrl: image ? URL.createObjectURL(image) : undefined,
                                            }
                                        ]));
                                    }
                                }}
                                onFaviconProcessed={onFaviconProcessed}
                                treatAsFavicon={key === 'favicon'}
                                initialFaviconPngDataUri={key === 'favicon' ? faviconPngDataUri : undefined}
                                initialFaviconIcoDataUri={key === 'favicon' ? faviconIcoDataUri : undefined}
                            />
                        ))
                        : null
                    }
                </Box>
            </Box>
        )
    }